// R45 + FASE 2 punt 5+6: Slim component dat leest uit gedeelde lineCache.
// FASE 2 — toont per regel kleurnummer (uit Lightspeed custom_fields) en een
// interne notitie-textarea (alleen intern, niet in pakbon of klant-mail).
import { useEffect, useMemo, useState } from "react";
import { Image as ImageIcon, ImageOff, Loader2, Save, StickyNote, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  fetchOrderLines,
  getCachedLines,
  type Line,
} from "@/lib/orderLinesPrefetch";
import {
  useOrderLineNotes,
  useSaveOrderLineNote,
  useDeleteOrderLineNote,
  type OrderLineNote,
} from "@/hooks/useOrderLineNotes";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Props {
  orderid: number;
  ordernumber?: string;
}

const OrderLinesExpansion = ({ orderid, ordernumber }: Props) => {
  const [lines, setLines] = useState<Line[] | null>(getCachedLines(orderid));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(new Set());

  const { data: notes } = useOrderLineNotes(orderid);
  const saveNote = useSaveOrderLineNote();
  const deleteNote = useDeleteOrderLineNote();
  const { user } = useAuth();
  const createdBy = (user as any)?.username ?? (user as any)?.roepnaam ?? (user as any)?.naam ?? null;

  // Per line: drafts en huidige bestaande notitie matchen op sku+lineitemindex
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const noteFor = useMemo(() => {
    const m = new Map<string, OrderLineNote>();
    for (const n of (notes ?? [])) {
      const k = `${n.lineitemindex ?? -1}|${n.sku ?? ""}`;
      m.set(k, n);
    }
    return m;
  }, [notes]);

  useEffect(() => {
    if (lines) return;
    let cancelled = false;
    setLoading(true);
    fetchOrderLines(orderid, ordernumber)
      .then((d) => { if (!cancelled) { setLines(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [orderid, ordernumber, lines]);

  if (loading && !lines) {
    return (
      <div className="rounded-md border bg-muted/30">
        <ul className="divide-y">
          {[0, 1, 2].map((i) => (
            <li key={i} className="grid grid-cols-[64px_1fr_auto] md:grid-cols-[56px_1fr_90px_70px] gap-3 px-3 py-3 items-center min-h-[64px]">
              <div className="h-16 w-16 md:h-12 md:w-12 shrink-0 rounded bg-muted animate-pulse" />
              <div className="min-w-0 space-y-2">
                <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
              </div>
              <div className="hidden md:block h-3 w-16 rounded bg-muted animate-pulse" />
              <div className="hidden md:block h-3 w-8 ml-auto rounded bg-muted animate-pulse" />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (error) {
    return <div className="px-2 py-4 text-sm text-destructive">Kon productregels niet laden, probeer later opnieuw.</div>;
  }
  if (!lines || lines.length === 0) {
    return <div className="px-2 py-4 text-sm text-muted-foreground">Geen productregels gevonden</div>;
  }

  const handleSave = async (line: Line, existing?: OrderLineNote) => {
    const key = `${line.lineitemindex}|${line.sku ?? ""}`;
    const draft = drafts[key] ?? existing?.note ?? "";
    if (!draft.trim()) {
      toast.error("Notitie is leeg");
      return;
    }
    try {
      await saveNote.mutateAsync({
        orderid,
        ordernumber: ordernumber ?? null,
        lineitemindex: line.lineitemindex,
        sku: line.sku,
        note: draft.trim(),
        createdBy,
        existingId: existing?.id ?? null,
      });
      toast.success("Notitie opgeslagen");
      setDrafts((d) => {
        const n = { ...d };
        delete n[key];
        return n;
      });
    } catch (e: any) {
      toast.error(`Opslaan mislukt: ${e?.message ?? e}`);
    }
  };

  const handleDelete = async (existing: OrderLineNote) => {
    try {
      await deleteNote.mutateAsync({ id: existing.id, orderid });
      toast.success("Notitie verwijderd");
    } catch (e: any) {
      toast.error(`Verwijderen mislukt: ${e?.message ?? e}`);
    }
  };

  return (
    <div className="max-h-[520px] overflow-y-auto overscroll-contain rounded-md border bg-muted/30">
      <div className="hidden md:grid grid-cols-[56px_1fr_90px_70px] gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b bg-muted/40">
        <div></div><div>Product</div><div>Eenheid</div><div className="text-right">Aantal</div>
      </div>
      <ul className="divide-y">
        {lines.map((l, idx) => {
          const showImg = !!l.image && !failedImageIds.has(l.id);
          const eager = idx < 5;
          const key = `${l.lineitemindex}|${l.sku ?? ""}`;
          const existing = noteFor.get(key);
          const draftValue = drafts[key] ?? existing?.note ?? "";
          const isSaving = saveNote.isPending;
          const hasNote = Boolean(existing);
          return (
            <li
              key={l.id}
              className="px-3 py-3 space-y-2"
            >
              <div className="grid grid-cols-[64px_1fr_auto] md:grid-cols-[56px_1fr_90px_70px] gap-3 items-center min-h-[64px]">
                <div className="h-16 w-16 md:h-12 md:w-12 shrink-0 rounded border bg-background flex items-center justify-center overflow-hidden">
                  {showImg ? (
                    <img
                      src={l.image as string}
                      alt=""
                      width={64}
                      height={64}
                      loading={eager ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={eager ? "high" : ("low" as any)}
                      className="h-full w-full object-cover"
                      onError={() => {
                        setFailedImageIds((prev) => {
                          if (prev.has(l.id)) return prev;
                          const next = new Set(prev);
                          next.add(l.id);
                          return next;
                        });
                      }}
                    />
                  ) : l.image ? (
                    <ImageOff className="h-6 w-6 text-muted-foreground/50" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium leading-snug line-clamp-2 break-words">{l.title}</div>
                  {l.variant && (
                    <div className="text-xs text-muted-foreground truncate hidden md:block">{l.variant}</div>
                  )}
                  {/* FASE 2 punt 6 — kleurnummer per regel */}
                  {l.kleur && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                      Kleur: <span className="tabular-nums">{l.kleur}</span>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground md:hidden mt-0.5">{l.qty} × {l.unit}</div>
                </div>
                <div className="hidden md:block text-sm text-muted-foreground truncate">{l.unit}</div>
                <div className="hidden md:block text-sm font-semibold text-right tabular-nums">{l.qty}×</div>
              </div>

              {/* FASE 2 punt 5 — interne notitie per regel (opvallender wanneer ingevuld) */}
              <div
                className={cn(
                  "ml-[76px] md:ml-[68px] flex flex-col gap-1.5 rounded-md p-1.5 transition-colors",
                  existing
                    ? "bg-amber-50 ring-1 ring-amber-300"
                    : "bg-transparent",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      "inline-flex items-center justify-center rounded-full p-1",
                      hasNote
                        ? "bg-teal-500 text-white ring-2 ring-teal-200"
                        : "bg-muted text-muted-foreground",
                    )}
                    title={hasNote ? "Deze regel heeft een interne notitie" : "Nog geen notitie"}
                  >
                    <StickyNote className={cn("h-3 w-3", hasNote && "fill-current")} />
                    {hasNote && (
                      <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-teal-100" />
                    )}
                  </span>
                  <span className={cn("text-[10px] font-semibold uppercase tracking-wide", hasNote ? "text-teal-700" : "text-muted-foreground")}>
                    {hasNote ? "Interne notitie" : "Notitie (intern)"}
                  </span>
                </div>
                <Textarea
                  value={draftValue}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [key]: e.target.value }))
                  }
                  placeholder="Interne notitie (alleen intern, niet zichtbaar voor klant)"
                  rows={2}
                  className={cn("text-xs min-h-[44px]", existing && "bg-white")}
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={isSaving || draftValue.trim() === (existing?.note ?? "")}
                    onClick={() => handleSave(l, existing)}
                    className="h-7 text-xs"
                  >
                    {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    {existing ? "Bijwerken" : "Opslaan"}
                  </Button>
                  {existing && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(existing)}
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      disabled={deleteNote.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                      Verwijderen
                    </Button>
                  )}
                  {existing && (
                    <span className="text-[10px] text-muted-foreground">
                      {existing.created_by ? `door ${existing.created_by}` : ""}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OrderLinesExpansion;
